import WebApp from '@twa-dev/sdk';

/**
 * Telegram WebApp utilities
 */

export class TelegramApp {
  private static instance: TelegramApp;
  private initialized = false;

  private constructor() {
    this.init();
  }

  static getInstance(): TelegramApp {
    if (!TelegramApp.instance) {
      TelegramApp.instance = new TelegramApp();
    }
    return TelegramApp.instance;
  }

  /**
   * Initialize Telegram WebApp
   */
  init() {
    if (this.initialized) return;

    try {
      WebApp.ready();
      WebApp.expand();
      
      // Enable closing confirmation
      WebApp.enableClosingConfirmation();
      
      this.initialized = true;
    } catch (error) {
      console.error('Failed to initialize Telegram WebApp:', error);
    }
  }

  /**
   * Get user data from Telegram
   */
  getUser() {
    return WebApp.initDataUnsafe.user || null;
  }

  /**
   * Get init data for backend verification
   */
  getInitData() {
    return WebApp.initData;
  }

  /**
   * Get theme parameters
   */
  getTheme() {
    return WebApp.themeParams;
  }

  /**
   * Check if app is in dark mode
   */
  isDarkMode() {
    return WebApp.colorScheme === 'dark';
  }

  /**
   * Show main button
   */
  showMainButton(text: string, onClick: () => void) {
    WebApp.MainButton.setText(text);
    WebApp.MainButton.show();
    WebApp.MainButton.onClick(onClick);
  }

  /**
   * Hide main button
   */
  hideMainButton() {
    WebApp.MainButton.hide();
  }

  /**
   * Show back button
   */
  showBackButton(onClick: () => void) {
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(onClick);
  }

  /**
   * Hide back button
   */
  hideBackButton() {
    WebApp.BackButton.hide();
  }

  /**
   * Haptic feedback
   */
  haptic(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') {
    try {
      WebApp.HapticFeedback.impactOccurred(style);
    } catch (error) {
      // Haptic not available
    }
  }

  /**
   * Notification haptic
   */
  notificationHaptic(type: 'error' | 'success' | 'warning') {
    try {
      WebApp.HapticFeedback.notificationOccurred(type);
    } catch (error) {
      // Haptic not available
    }
  }

  /**
   * Show popup
   */
  showPopup(title: string, message: string, buttons?: string[]) {
    return new Promise<string>((resolve) => {
      WebApp.showPopup(
        {
          title,
          message,
          buttons: buttons?.map(text => ({ type: 'default', text })) || [{ type: 'close' }],
        },
        (buttonId?: string) => {
          resolve(buttonId || '');
        }
      );
    });
  }

  /**
   * Show confirm dialog
   */
  showConfirm(message: string) {
    return new Promise<boolean>((resolve) => {
      WebApp.showConfirm(message, (confirmed) => {
        resolve(confirmed);
      });
    });
  }

  /**
   * Close app
   */
  close() {
    WebApp.close();
  }

  /**
   * Open link
   */
  openLink(url: string) {
    WebApp.openLink(url);
  }

  /**
   * Open Telegram link
   */
  openTelegramLink(url: string) {
    WebApp.openTelegramLink(url);
  }

  /**
   * Share to story
   */
  shareToStory(mediaUrl: string) {
    WebApp.shareToStory(mediaUrl);
  }

  /**
   * Set header color
   */
  setHeaderColor(color: string) {
    WebApp.setHeaderColor(color as any);
  }

  /**
   * Set background color
   */
  setBackgroundColor(color: string) {
    WebApp.setBackgroundColor(color as any);
  }
}

// Export singleton instance
export const telegram = TelegramApp.getInstance();

